// packages/web/src/components/layout/layouts/PartnerAdminLayout.tsx
// FAANG-GRADE: Dedicated layout for partner administrators
// Business operations focus with partner branding and management tools

'use client';

import { Avatar, Box, IconButton, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { apiFetch } from '../../../lib/api';
import { logout } from '../../../lib/auth/logout';
import BottomNavigation from '../BottomNavigation';
import DeploymentIndicator from '../DeploymentIndicator';
import HeaderBranding from '../HeaderBranding';
import { BaseHeader } from '../headers/BaseHeader';
import MarketplaceHeaderElements from '../MarketplaceHeaderElements';
import { useAuthContext } from '../providers/AuthProvider';

interface PartnerBranding {
  name: string;
  logo?: string;
  primaryColor: string;
  secondaryColor: string;
  website?: string;
  headerText?: string;
}

interface PartnerAdminLayoutProps {
  children: React.ReactNode;
}

export const PartnerAdminLayout: React.FC<PartnerAdminLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const router = useRouter();
  const { user, role } = useAuthContext();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [partnerBranding, setPartnerBranding] = useState<PartnerBranding | null>(null);

  // Stable references for useEffect dependencies
  const userId = user?.id;
  const tenantId = user?.tenantId;

  // Fetch partner branding with caching (matches original Layout.tsx logic)
  useEffect(() => {
    const fetchPartnerBranding = async () => {
      if (role === 'partner_admin' && user && tenantId) {
        // Check sessionStorage cache first
        const cacheKey = `partner_branding_${tenantId}`;
        const cached = sessionStorage.getItem(cacheKey);

        if (cached) {
          try {
            const cachedData = JSON.parse(cached);
            setPartnerBranding(cachedData);
            return;
          } catch (error) {
            sessionStorage.removeItem(cacheKey);
          }
        }

        // Fetch from API if not cached
        if (!partnerBranding) {
          try {
            const brandingData = await apiFetch('/partners/settings/profile');
            const branding: PartnerBranding = {
              name: brandingData.name,
              logo: brandingData.branding?.logoUrl,
              primaryColor: brandingData.branding?.primaryColor || '#1976d2',
              secondaryColor: brandingData.branding?.secondaryColor || '#dc004e',
              website: brandingData.company?.websiteUrl,
              headerText: brandingData.branding?.headerText,
            };

            setPartnerBranding(branding);

            // Cache for 5 minutes
            sessionStorage.setItem(cacheKey, JSON.stringify(branding));
            setTimeout(() => sessionStorage.removeItem(cacheKey), 5 * 60 * 1000);
          } catch (error: any) {
            console.error('[PartnerAdminLayout] Failed to fetch partner branding:', error);
            // Set default branding on error
            setPartnerBranding({
              name: user.email?.split('@')[0] || 'Partner',
              logo: undefined,
              primaryColor: '#1976d2',
              secondaryColor: '#dc004e',
              website: undefined,
            });
          }
        }
      } else {
        setPartnerBranding(null);
      }
    };

    fetchPartnerBranding();
  }, [role, userId, tenantId]); // Use stable references to prevent infinite re-renders

  const handleProfileMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    console.log('[PartnerAdminLayout] Profile menu open clicked');
    setAnchorEl(event.currentTarget);
  }, []);

  const handleProfileMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleLogout = useCallback(async () => {
    handleProfileMenuClose();
    // FAANG-GRADE: Use unified logout function - single source of truth
    await logout();
  }, [handleProfileMenuClose]);

  const handleProfileClick = useCallback(() => {
    handleProfileMenuClose();
    router.push('/partners/profile');
  }, [handleProfileMenuClose, router]);

  const branding = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: partnerBranding ? 3 : 2,
          transition: 'all 0.2s ease',
        }}
      >
        <HeaderBranding size="large" clickable={true} branding={partnerBranding || undefined} />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            {partnerBranding?.headerText || ''}
          </Typography>
          {partnerBranding && (
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Partner Portal
            </Typography>
          )}
        </Box>
      </Box>
    ),
    [partnerBranding],
  );

  // Memoize avatar letter to prevent unnecessary re-renders
  const avatarLetter = useMemo(
    () =>
      (user as any)?.firstName?.charAt(0) ||
      (user as any)?.first_name?.charAt(0) ||
      user?.email?.charAt(0) ||
      '',
    [user],
  );

  const actions = useMemo(
    () => (
      <IconButton
        id="user-menu-button"
        edge="end"
        aria-label="User menu"
        aria-haspopup="menu"
        aria-controls={anchorEl ? 'user-menu' : undefined}
        aria-expanded={anchorEl ? 'true' : undefined}
        data-testid="user-menu-button"
        onClick={handleProfileMenuOpen}
        color="inherit"
      >
        <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
          {avatarLetter}
        </Avatar>
      </IconButton>
    ),
    [handleProfileMenuOpen, theme.palette.primary.main, avatarLetter],
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <BaseHeader branding={branding} actions={actions}>
        <MarketplaceHeaderElements />
      </BaseHeader>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: '100%',
          pb: 7, // Bottom padding for navigation
        }}
      >
        {/* Toolbar spacer for fixed header */}
        <Box sx={{ minHeight: '64px' }} />

        {children}
      </Box>

      <BottomNavigation
        role={role}
        activePath={router.pathname}
        onNavigate={(path) => router.push(path)}
      />

      {/* <AskTechaddsWidget /> - Disabled for partner admin pages */}
      <DeploymentIndicator />

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        MenuListProps={{ 'aria-labelledby': 'user-menu-button' }}
      >
        <MenuItem onClick={handleProfileClick}>Profile</MenuItem>
        <MenuItem onClick={handleLogout} data-testid="user-menu-logout">
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};
